

/************* 1) Ensure GSAP via site-wide loader (with fallback) *************/
async function ensureGSAPWithPlugins(pluginNames = []) {
  // Prefer your global loader (added in gsap-loader.js)
  if (window.__gsapLoader?.plugins) {
    const { gsap } = await window.__gsapLoader.plugins(pluginNames);
    return { gsap, ...Object.fromEntries(pluginNames.map(n => [n, window[n]])) };
  }

  // Fallback self-loader using the asset map you set in theme.liquid
  const A = window.__GSAP_ASSETS || {};
  const loadScript = (src) => new Promise((res, rej) => {
    if (!src) return rej(new Error('Missing script src'));
    // de-dupe: reuse any in-flight/completed script
    const sel = `script[data-gsap-src="${src}"]`;
    const existing = document.querySelector(sel);
    if (existing?.dataset.loaded === 'true') return res();
    if (existing) {
      existing.addEventListener('load', res, { once: true });
      existing.addEventListener('error', () => rej(new Error('Failed ' + src)), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.async = true;
    s.dataset.gsapSrc = src;
    s.onload = () => { s.dataset.loaded = 'true'; res(); };
    s.onerror = () => rej(new Error('Failed ' + src));
    s.src = src;
    document.head.appendChild(s);
  });

  if (!window.gsap && A.core) await loadScript(A.core);
  for (const p of pluginNames) {
    if (!window[p] && A[p]) await loadScript(A[p]);
  }

  // tiny readiness wait-loop
  const t0 = performance.now();
  while (!window.gsap || pluginNames.some(p => !window[p])) {
    if (performance.now() - t0 > 8000) break;
    await new Promise(r => setTimeout(r, 25));
  }
  const gsap = window.gsap;
  return { gsap, ...Object.fromEntries(pluginNames.map(n => [n, window[n]])) };
}

/************* 2) Flip-on-scroll logic *************/
const ROOT_SEL = '[data-flip-root]';
const state = new WeakMap(); // per-root: { tl, st, onVC, onResize }

function destroy(root) {
  const s = state.get(root);
  if (!s) return;
  try { s.tl && s.tl.kill(); } catch {}
  try { s.st && s.st.kill(); } catch {}
  if (s.onVC) document.removeEventListener('visibilitychange', s.onVC);
  if (s.onResize) window.removeEventListener('resize', s.onResize);
  state.delete(root);
}

function setup(root) {
  const { gsap, ScrollTrigger, Flip } = window;
  gsap.registerPlugin(ScrollTrigger, Flip);

  // avoid double init
  destroy(root);

  const wrappers = Array.from(root.querySelectorAll(`[data-flip-element="wrapper"]`));
  const target = root.querySelector(`[data-flip-element="target"]`);
  if (!target || wrappers.length < 2) return; // need at least 2 anchors

  let tl;

  const build = () => {
    // kill prior
    if (tl) {
      tl.kill();
      gsap.set(target, { clearProps: 'all' });
    }

    // Compute distances between the vertical centers of consecutive wrappers
    const pageY = () => window.pageYOffset || document.documentElement.scrollTop || 0;

    const centers = wrappers.map(el => {
      const r = el.getBoundingClientRect();
      return (r.top + pageY()) + (el.offsetHeight / 2);
    });

    // durations proportional to scroll distance (timeline will map total duration to ST distance)
    const segDurations = [];
    for (let i = 0; i < centers.length - 1; i++) {
      segDurations.push(Math.max(1, centers[i + 1] - centers[i])); // ≥1 to avoid zero
    }

    // total scroll distance (px) becomes the ST end distance
    const totalDistance = segDurations.reduce((a, b) => a + b, 0);

    // Use the first & last wrapper as ScrollTrigger bounds
    tl = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: wrappers[0],
        start: 'center center',
        endTrigger: wrappers[wrappers.length - 1],
        // Map the total timeline duration to visual distance:
        // Using end as "center center" on last + scrub is enough because
        // timeline durations are relative; but we also guard with a minimum distance.
        end: 'center center',
        scrub: 0.25,
        invalidateOnRefresh: true,
      }
    });

    // Seed the target into the first wrapper so Flip.fit has a valid "from"
    const firstWrapper = wrappers[0];
    if (!firstWrapper.contains(target)) {
      firstWrapper.appendChild(target);
    }

    // Build the sequence of Flip.fit tweens
    wrappers.forEach((wrap, i) => {
      const next = wrappers[i + 1];
      if (!next) return;

      // Each segment duration is proportional to actual scroll distance,
      // but scrub maps the whole TL to the ST range, so these act as weights.
      const d = segDurations[i];

      tl.add(
        Flip.fit(target, next, {
          // Do NOT use seconds; it’s proportional. The entire TL gets scaled to ST distance.
          duration: d,
          ease: 'none'
        })
      );
    });

    // Keep ScrollTrigger in sync with any layout shifts
    requestAnimationFrame(() => ScrollTrigger.refresh());
  };

  // Build once
  build();

  // Pause when tab hidden
  const onVC = () => (document.hidden ? tl?.pause() : tl?.play());
  document.addEventListener('visibilitychange', onVC);

  // Debounced rebuild on resize (fonts, layout, breakpoints)
  let resizeTimer;
  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      build();
    }, 120);
  };
  window.addEventListener('resize', onResize, { passive: true });

  // store
  const st = ScrollTrigger.getById?.('__flip_on_scroll__'); // not used, but reserved if you add id
  state.set(root, { tl, st, onVC, onResize });
}

/************* 3) Boot + Theme Editor hooks *************/
(async () => {
  const { gsap, ScrollTrigger, Flip } = await ensureGSAPWithPlugins(['ScrollTrigger', 'Flip']);
  if (!gsap || !ScrollTrigger || !Flip) return;

  const initAll = () => document.querySelectorAll(ROOT_SEL).forEach(setup);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  // Theme Editor lifecycle
  document.addEventListener('shopify:section:load', (e) => {
    const sec = document.getElementById(`shopify-section-${e.detail.sectionId}`);
    if (!sec) return;
    const root = sec.querySelector(ROOT_SEL);
    if (root) setup(root);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sec = document.getElementById(`shopify-section-${e.detail.sectionId}`);
    if (!sec) return;
    const root = sec.querySelector(ROOT_SEL);
    if (root) destroy(root);
  });

  const rebuild = () => {
    document.querySelectorAll(ROOT_SEL).forEach(root => {
      destroy(root);
      setup(root);
    });
    requestAnimationFrame(() => ScrollTrigger?.refresh());
  };
  document.addEventListener('shopify:section:select', rebuild);
  document.addEventListener('shopify:section:deselect', rebuild);
  document.addEventListener('shopify:block:select', rebuild);
  document.addEventListener('shopify:block:deselect', rebuild);

  window.addEventListener('orientationchange', () => {
    requestAnimationFrame(() => ScrollTrigger?.refresh());
  }, { passive: true });
})();
